import { useMemo } from 'react';
import { useAccount } from '../../lib/account/useAccount';
import { useAccountingAccess } from '../../hooks/useAccountingAccess';
import { useCopy } from '../../hooks/useCopy';
import { useHasActiveLedger } from '../../hooks/useHasActiveLedger';
import { useInvoicingVisible } from '../../hooks/useInvoicingVisible';
import { buildDestinations, type Destination } from './omnibox.destinations';
import { matchDestinations } from './omnibox.match';
import type { OmniboxGroup } from './omnibox.items';

/** Few enough that "Go to" never pushes the records below the fold. */
const DESTINATIONS_SHOWN = 3;

/**
 * The "Go to" group for what the user has typed: pages, settings and accounting
 * reports whose name or keywords match. Synchronous and local, so it can lead
 * the list without moving when the search results land.
 *
 * `group` is null for empty text or no match. `destinationFor` turns a row id
 * (the destination's catalogue key) back into the destination.
 */
export function useOmniboxDestinations({ text }: { text: string }): {
  group: OmniboxGroup | null;
  destinationFor: (key: string) => Destination | undefined;
} {
  const copy = useCopy();
  const { account, administeredBy, canManageHumanTeam, isLoading: accountLoading } = useAccount();
  const accountingAccess = useAccountingAccess();
  const invoicingVisible = useInvoicingVisible();
  const { hasActiveLedger, hasActiveTrustLedger } = useHasActiveLedger();
  const administered = !!administeredBy;
  const practiceType = account?.practiceSettings?.practiceType ?? null;

  const destinations = useMemo(
    () =>
      buildDestinations(
        {
          administered,
          accountLoading,
          practiceType,
          accountingAccess,
          invoicingVisible,
          hasActiveLedger,
          hasActiveTrustLedger,
          canManageHumanTeam,
        },
        copy,
      ),
    [
      administered,
      accountLoading,
      practiceType,
      accountingAccess,
      invoicingVisible,
      hasActiveLedger,
      hasActiveTrustLedger,
      canManageHumanTeam,
      copy,
    ],
  );

  const matches = useMemo(
    () => matchDestinations(destinations, text, DESTINATIONS_SHOWN),
    [destinations, text],
  );

  const group: OmniboxGroup | null =
    matches.length === 0
      ? null
      : {
          heading: copy('omnibox:groups.goTo'),
          rows: matches.map((match) => ({
            kind: 'destination',
            id: match.key,
            label: match.label,
            sublabel: match.location,
          })),
        };

  return { group, destinationFor: (key) => destinations.find((destination) => destination.key === key) };
}
